import React, { useState, useEffect } from 'react';
import { FaPlay, FaEye, FaShare } from 'react-icons/fa';
import { pdfActivityService } from '../services/pdfActivityService';
import ConsistentLoader from './ConsistentLoader';

const ActivityJourneyDisplay = () => {
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const activitiesData = await pdfActivityService.getActivitiesFromFirestore();
      // Take first 5 activities
      setActivities(activitiesData.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error loading activities:', error);
      setLoading(false);
    }
  };

  const getActivityIcon = (number) => {
    const icons = [FaPlay, FaEye, FaPlay, FaEye, FaPlay];
    return icons[(number - 1) % icons.length];
  };

  const getActivityColor = (number) => {
    const colors = [
      'from-purple-500 to-pink-500',
      'from-blue-500 to-cyan-500', 
      'from-green-500 to-emerald-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-purple-500'
    ];
    return colors[(number - 1) % colors.length];
  };

  const createUserJourney = (activity) => {
    const journeys = {
      1: {
        title: "Creative Discovery Journey",
        steps: [
          "Begin with a moment of quiet reflection",
          "Gather your favorite art materials",
          "Let your imagination flow freely",
          "Create without judgment or expectation",
          "Celebrate your unique artistic voice"
        ]
      },
      2: {
        title: "Emotional Expression Path",
        steps: [
          "Connect with your inner feelings",
          "Choose colors that resonate with your mood",
          "Express emotions through movement and color",
          "Allow vulnerability to guide your creation",
          "Find beauty in emotional authenticity"
        ]
      },
      3: {
        title: "Innovation & Inspiration Route",
        steps: [
          "Open your mind to new possibilities",
          "Explore unconventional materials and techniques",
          "Break free from traditional boundaries",
          "Let curiosity be your compass",
          "Transform inspiration into unique creations"
        ]
      },
      4: {
        title: "Excellence & Mastery Journey",
        steps: [
          "Focus on refining your technique",
          "Practice with intention and purpose",
          "Learn from each brushstroke and decision",
          "Build confidence through consistent effort",
          "Achieve mastery through dedicated practice"
        ]
      },
      5: {
        title: "Playful Exploration Adventure",
        steps: [
          "Approach art with childlike wonder",
          "Experiment with different styles and mediums",
          "Embrace mistakes as learning opportunities",
          "Find joy in the creative process",
          "Discover your artistic voice through play"
        ]
      }
    };
    return journeys[activity.number] || journeys[1];
  };

  if (loading) {
    return <ConsistentLoader message="Loading your creative journey..." />;
  }

  // Artist Wall background image
  const artistwallBackground = '/static/media/artistwall.9b21b08257edb5c12576.png';

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${artistwallBackground})` }}
    >
      {/* Overlay for readability */}
      <div className="min-h-screen bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-amber-800/20 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
              Your Creative Journey Awaits
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow-md">
              Discover five beautiful activities designed to awaken your inner artist and guide you through the Artmosphere experience
            </p>
          </div>

          {/* Activities Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {activities.map((activity) => {
              const IconComponent = getActivityIcon(activity.number);
              const gradientClass = getActivityColor(activity.number);
              const journey = createUserJourney(activity);

              return (
                <div
                  key={activity.id}
                  className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
                  onClick={() => setSelectedActivity(activity)}
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-amber-200 overflow-hidden hover:shadow-2xl transition-all duration-300">
                    {/* Header */}
                    <div className={`bg-gradient-to-r ${gradientClass} p-6 text-white relative overflow-hidden`}>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-3">
                          <IconComponent className="text-3xl" />
                          <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
                            Activity {activity.number}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold mb-2 line-clamp-2">
                          {activity.title}
                        </h3>
                        <p className="text-sm opacity-90 line-clamp-2">
                          {activity.description}
                        </p>
                      </div>
                    </div>

                    {/* Journey Preview */}
                    <div className="p-6">
                      <h4 className="font-semibold text-amber-800 mb-3">
                        {journey.title}
                      </h4>
                      <div className="space-y-2">
                        {journey.steps.slice(0, 3).map((step, index) => (
                          <div key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-sm text-amber-700 line-clamp-2">{step}</p>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between">
                        <button className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center">
                          <FaEye className="mr-1" />
                          View Journey
                        </button>
                        <div className="flex space-x-2">
                          <button className="p-2 text-amber-600 hover:bg-amber-50 rounded-full transition-colors">
                            <FaShare className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-amber-600 hover:bg-amber-50 rounded-full transition-colors">
                            <FaPlay className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected Activity Modal */}
          {selectedActivity && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      {(() => {
                        const IconComponent = getActivityIcon(selectedActivity.number);
                        const gradientClass = getActivityColor(selectedActivity.number);
                        return (
                          <div className={`w-16 h-16 bg-gradient-to-r ${gradientClass} rounded-2xl flex items-center justify-center mr-4`}>
                            <IconComponent className="text-2xl text-white" />
                          </div>
                        );
                      })()}
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                          Activity {selectedActivity.number}
                        </h2>
                        <p className="text-amber-600 font-medium">Creative Journey</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedActivity(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {selectedActivity.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedActivity.description}
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-8">
                    <h4 className="text-lg font-semibold text-amber-800 mb-4">
                      Your Creative Journey
                    </h4>
                    <div className="space-y-4">
                      {createUserJourney(selectedActivity).steps.map((step, index) => (
                        <div key={index} className="flex items-start">
                          <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-4 flex-shrink-0">
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-gray-700 font-medium">{step}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 flex items-center justify-center">
                      <FaPlay className="mr-2" />
                      Start This Journey
                    </button>
                    <button className="px-6 py-3 border-2 border-amber-300 text-amber-700 rounded-xl font-semibold hover:bg-amber-50 transition-colors">
                      Save for Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityJourneyDisplay;
